import { formatDate } from '@perch-labs/warbler';
import React from 'react';
import { copyFor } from 'config';
import { Crate, FlexCrate, Icon, Tooltip } from '@perch-labs/shrike';
import {
  StyledListItem,
  StyledContainer,
  StyledSupportingText,
  StyledHeading,
  StyledTooltipContainer,
} from '../line-items.style';
import { LineItemAmount } from '../line-item-amount.component';

interface Props {
  lineItem: CorrectionLineItem;
  includeSupportingText: boolean;
  includeTooltips: boolean;
}

export const CorrectionLineItem = ({
  lineItem,
  includeSupportingText,
  includeTooltips,
}: Props) => {
  let getCopy;

  switch (lineItem.key) {
    case 'debit_adjustment':
    case 'undercharge_correction':
      getCopy = lineItem.correctingStatement
        ? copyFor('billing.statement.lineItems.underChargeCorrection')
        : copyFor('billing.statement.lineItems.missedUnderChargeCorrection');
      break;
    case 'credit_adjustment':
    case 'overcharge_correction':
    default:
      getCopy = lineItem.correctingStatement
        ? copyFor('billing.statement.lineItems.overChargeCorrection')
        : copyFor('billing.statement.lineItems.missedOverChargeCorrection');
      break;
  }

  const subtitle = getCopy('subtitle', {
    date: formatDate(lineItem.correctingStatement?.issuedAt, {
      format: 'MMM d, yyyy',
    }),
    // eslint-disable-next-line no-nested-ternary
    periodName: lineItem.creditCorrection?.serviceStartDate
      ? getCopy('servicePeriod', {
          periodStartDate: formatDate(
            lineItem.creditCorrection?.serviceStartDate,
            {
              format: 'MMM d, yyyy',
            }
          ),
          periodEndDate: formatDate(lineItem.creditCorrection?.serviceEndDate, {
            format: 'MMM d, yyyy',
          }),
        })
      : lineItem.creditCorrection?.productionStartDate
      ? getCopy('productionPeriod', {
          periodStartDate: formatDate(
            lineItem.creditCorrection?.productionStartDate,
            {
              format: 'MMM d, yyyy',
            }
          ),
          periodEndDate: formatDate(
            lineItem.creditCorrection?.productionEndDate,
            {
              format: 'MMM d, yyyy',
            }
          ),
        })
      : getCopy('previousPeriod'),
  });

  return (
    <StyledListItem data-testid="correction-line-item">
      <StyledContainer>
        <FlexCrate flexDirection="column" gap="8px">
          <StyledTooltipContainer>
            <StyledHeading>{getCopy('title')}</StyledHeading>
            {includeTooltips && (
              <Tooltip
                content={subtitle}
                tooltipId="correctionLineItem"
                label={getCopy('tooltip.label')}
              >
                <Icon icon="InfoCircleInverted" />
              </Tooltip>
            )}
          </StyledTooltipContainer>
          {includeSupportingText && (
            <Crate padding="0 64px 0 0">
              <StyledSupportingText>{subtitle}</StyledSupportingText>
            </Crate>
          )}
        </FlexCrate>
        <LineItemAmount amountCents={lineItem.amountCents} />
      </StyledContainer>
    </StyledListItem>
  );
};
